import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateField, AmountField, PriceField, CryptoField } from '../molecules/FormFields';
import { FormField } from '../atoms/FormField';
import { Input } from '../atoms/Input';
import { useCryptoPriceAtDate } from '../../utils/hooks/useCryptoPriceAtDate';
import { formatCurrency } from '../../utils/date';

/**
 * Swap transaction fields component for use within FormProvider
 * Handles cryptocurrency swap transactions with from/to crypto
 */
interface SwapTransactionFieldsProps {
  prefix?: string;
  isFetchingSentPrice?: boolean;
  isFetchingReceivedPrice?: boolean;
}

export const SwapTransactionFields: React.FC<SwapTransactionFieldsProps> = ({
  prefix = '',
}) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext();

  // Build field names with optional prefix
  const fieldName = (field: string) => prefix ? `${prefix}.${field}` : field;

  // Watch form values for real-time calculations and price fetching
  const date = watch(fieldName('date'));
  const sentCrypto = watch(fieldName('sentCrypto'));
  const sentAmount = watch(fieldName('sentAmount')) || 0;
  const sentUnitPrice = watch(fieldName('sentUnitPrice')) || 0;
  const receivedCrypto = watch(fieldName('receivedCrypto'));
  const receivedAmount = watch(fieldName('receivedAmount')) || 0;
  const receivedUnitPrice = watch(fieldName('receivedUnitPrice')) || 0;

  // Fetch sent crypto price automatically
  const { data: sentCryptoPrice, isLoading: isFetchingSent } = useCryptoPriceAtDate({
    coinId: sentCrypto?.id || '',
    date: date || '',
    enabled: !!sentCrypto && !!date,
  });

  // Fetch received crypto price automatically
  const { data: receivedCryptoPrice, isLoading: isFetchingReceived } = useCryptoPriceAtDate({
    coinId: receivedCrypto?.id || '',
    date: date || '',
    enabled: !!receivedCrypto && !!date,
  });

  // Update form when sent price is fetched
  useEffect(() => {
    if (sentCryptoPrice !== null && sentCryptoPrice !== undefined) {
      setValue(fieldName('sentUnitPrice'), sentCryptoPrice);
    }
  }, [sentCryptoPrice, fieldName, setValue]);

  // Update form when received price is fetched
  useEffect(() => {
    if (receivedCryptoPrice !== null && receivedCryptoPrice !== undefined) {
      setValue(fieldName('receivedUnitPrice'), receivedCryptoPrice);
    }
  }, [receivedCryptoPrice, fieldName, setValue]);

  // Calculate total values
  const sentTotalValue = sentAmount && sentUnitPrice
    ? parseFloat(sentAmount.toString()) * parseFloat(sentUnitPrice.toString())
    : 0;

  const receivedTotalValue = receivedAmount && receivedUnitPrice
    ? parseFloat(receivedAmount.toString()) * parseFloat(receivedUnitPrice.toString())
    : 0;

  // Auto-fill fees with the positive difference between sent and received values
  useEffect(() => {
    if (sentTotalValue > 0 && receivedTotalValue > 0) {
      const diff = Math.max(0, sentTotalValue - receivedTotalValue);
      setValue(fieldName('fees'), parseFloat(diff.toFixed(2)));
    }
  }, [sentTotalValue, receivedTotalValue, fieldName, setValue]);

  // Get error messages for nested fields
  const getError = (field: string) => {
    const fieldPath = fieldName(field).split('.');
    let error: unknown = errors;
    for (const part of fieldPath) {
      error = (error as Record<string, unknown>)?.[part];
    }
    return (error as { message?: string })?.message;
  };

  return (
    <>
      {/* Date Section */}
      <DateField
        name={fieldName('date')}
        label="Date du swap"
        required
        type="datetime-local"
      />

      {/* Sent Crypto Section */}
      <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Crypto envoyée</h3>
        <div className="space-y-4">
          <CryptoField
            name={fieldName('sentCrypto')}
            label="Cryptomonnaie envoyée"
            required
            placeholder="Sélectionnez la crypto envoyée"
          />

          <AmountField
            name={fieldName('sentAmount')}
            label="Montant envoyé"
            required
            step="0.00000001"
            placeholder="Entrez le montant envoyé"
          />

          <PriceField
            name={fieldName('sentUnitPrice')}
            label="Prix unitaire EUR (au moment du swap)"
            required
            placeholder="Entrez le prix unitaire"
            isFetching={isFetchingSent}
          />
        </div>
      </div>

      {/* Received Crypto Section */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Crypto reçue</h3>
        <div className="space-y-4">
          <CryptoField
            name={fieldName('receivedCrypto')}
            label="Cryptomonnaie reçue"
            required
            placeholder="Sélectionnez la crypto reçue"
          />

          <AmountField
            name={fieldName('receivedAmount')}
            label="Montant reçu"
            required
            step="0.00000001"
            placeholder="Entrez le montant reçu"
          />

          <PriceField
            name={fieldName('receivedUnitPrice')}
            label="Prix unitaire EUR (au moment du swap)"
            required
            placeholder="Entrez le prix unitaire"
            isFetching={isFetchingReceived}
          />
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Informations additionnelles</h3>
        <div className="space-y-4">
          <FormField label="Frais (EUR, optionnel)" error={getError('fees')}>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                placeholder="Entrez les frais"
                {...register(fieldName('fees'), {
                  setValueAs: (v: string) => v === '' || v === null ? undefined : parseFloat(v),
                })}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 absolute top-2 right-2">EUR</span>
            </div>
          </FormField>

          <FormField label="Plateforme (optionnel)" error={getError('platform')}>
            <Input
              type="text"
              placeholder="Ex: Binance, Kraken, Uniswap..."
              {...register(fieldName('platform'))}
            />
          </FormField>
        </div>
      </div>

      {/* Summary Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-indigo-200">
            <div>
              <p className="text-sm text-indigo-600 font-medium mb-1">Valeur envoyée</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {sentAmount} {sentCrypto?.symbol || ''} × {sentUnitPrice} EUR
              </p>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-indigo-600">
                {formatCurrency(sentTotalValue)}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4">
            <div>
              <p className="text-sm text-green-600 font-medium mb-1">Valeur reçue</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {receivedAmount} {receivedCrypto?.symbol || ''} × {receivedUnitPrice} EUR
              </p>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-green-600">
                {formatCurrency(receivedTotalValue)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
